/**
 * Меню користувача (layout.md, розд. 1): «Мій профіль» (PRF-01), тема, щільність, легкий режим, клавіші (PRF-08:
 * однолітерні, підказки в кнопках; шпаргалка `?`), «Очистити недавні записи» (UX-04: спільний
 * ПК), «Вийти» (user-cabinet.md, розд. 10.1; `platform/sign-out.ts`). Зміна діє одразу, без
 * перезавантаження (lite-mode.md, розд. 2).
 */
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { keys as k } from '../i18n/uk/keys.ts';
import { navigation as n } from '../i18n/uk/navigation.ts';
import { prefsText as p } from '../i18n/uk/prefs.ts';
import { save as sv } from '../i18n/uk/save.ts';
import { shell as t } from '../i18n/uk/shell.ts';
import { supportCore as sp } from '../i18n/uk/support-core.ts';
import { openCheatsheet } from '../palette/store.ts';
import { waitForWrites } from '../platform/api.ts';
import { mergeQueueEntities, usePlatformMeta } from '../platform/meta.ts';
import { clearRecentRecords } from '../platform/recent.ts';
import { signOut } from '../platform/sign-out.ts';
import { type Density, setPrefs, type Theme, usePrefs } from '../prefs/prefs.ts';
import { openReport } from '../support/report.ts';
import { Icon } from '../ui/Icon.tsx';
import { MenuButton, type MenuItem } from '../ui/Menu.tsx';
import { toast } from '../ui/notify/index.ts';
import { UnsavedDialog } from '../ui/save/UnsavedDialog.tsx';
import { hasUnsavedChanges } from '../ui/save/unsaved-registry.ts';
import { UserAvatar } from './avatar.tsx';
import { DUPLICATES_PATH, PROFILE_PATH, TRASH_PATH } from './nav-model.ts';
import type { ShellUser } from './user.ts';

/** Скільки «Вийти» чекає на збереження в дорозі, перш ніж питати людину (p95 збереження < 200 мс). */
const SIGN_OUT_WRITES_WAIT_MS = 3_000;

/** `user` — хто увійшов (shell/user.ts); `null` — ім'я ще не прийшло або входу немає. */
export function UserMenu({ user }: { user: ShellUser | null }) {
  const prefs = usePrefs();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Черга дублів (DAT-07) — лише ролям, що зливають записи сутності з правилами дублів.
  const mergeQueue = mergeQueueEntities(usePlatformMeta({ optional: true }).data).length > 0;
  // Діалог «вийти без збереження?» — ДО запиту виходу: після нього сесії вже немає.
  const [askSignOut, setAskSignOut] = useState(false);
  const doSignOut = () => {
    void signOut(queryClient).then((ok) => {
      if (!ok) toast({ text: t.signOutFailed, tone: 'alert' });
    });
  };
  const theme = (value: Theme, label: string): MenuItem => ({
    kind: 'radio',
    id: `theme-${value}`,
    label,
    checked: prefs.theme === value,
    onSelect: () => setPrefs({ theme: value }),
  });
  const density = (value: Density, label: string): MenuItem => ({
    kind: 'radio',
    id: `density-${value}`,
    label,
    checked: prefs.density === value,
    onSelect: () => setPrefs({ density: value }),
  });
  const items: MenuItem[] = [
    // «Мій профіль» (PRF-01) — лише після входу: профіль живе на сервері.
    ...(user?.signedIn
      ? ([
          {
            kind: 'item',
            id: 'profile',
            label: p.profileLink,
            onSelect: () => void navigate({ to: PROFILE_PATH }),
          },
          // Кошик людини (DAT-05; trash.html, кадр В1): рідко потрібен, тож у меню, а не в навігації.
          {
            kind: 'item',
            id: 'trash',
            label: t.trash,
            onSelect: () => void navigate({ to: TRASH_PATH }),
          },
          ...(mergeQueue
            ? ([
                {
                  kind: 'item',
                  id: 'duplicates',
                  label: t.duplicates,
                  onSelect: () => void navigate({ to: DUPLICATES_PATH }),
                },
              ] satisfies MenuItem[])
            : []),
          { kind: 'separator', id: 's0' },
        ] satisfies MenuItem[])
      : []),
    { kind: 'group', id: 'g-theme', label: t.menuTheme },
    theme('system', t.themeSystem),
    theme('light', t.themeLight),
    theme('dark', t.themeDark),
    { kind: 'separator', id: 's1' },
    { kind: 'group', id: 'g-density', label: t.menuDensity },
    density('comfortable', t.densityComfortable),
    density('compact', t.densityCompact),
    { kind: 'separator', id: 's2' },
    {
      kind: 'checkbox',
      id: 'lite',
      label: t.menuLite,
      checked: prefs.lite,
      onSelect: () => setPrefs({ lite: !prefs.lite }),
    },
    {
      kind: 'checkbox',
      id: 'single-keys',
      label: t.menuSingleKeys,
      checked: prefs.singleKeys,
      onSelect: () => setPrefs({ singleKeys: !prefs.singleKeys }),
    },
    {
      kind: 'checkbox',
      id: 'key-hints',
      label: k.menuKeyHints,
      checked: prefs.keyHints,
      onSelect: () => setPrefs({ keyHints: !prefs.keyHints }),
    },
    {
      kind: 'item',
      id: 'cheatsheet',
      label: k.menuCheatsheet,
      hint: '?',
      // Наступним кадром: меню спершу закривається й повертає фокус на кнопку — туди ж
      // повернеться фокус і після шпаргалки.
      onSelect: () => requestAnimationFrame(openCheatsheet),
    },
    {
      kind: 'item',
      id: 'report',
      label: sp.reportAction,
      // Меню довідки з'явиться з E3a-13 (UX-39): тоді пункт переїде туди. Фокус форми — на кнопку меню.
      onSelect: () => requestAnimationFrame(() => openReport()),
    },
    { kind: 'separator', id: 's3' },
    {
      kind: 'item',
      id: 'recent-clear',
      label: n.recentClear,
      // На сервері, для всіх пристроїв людини: про успіх кажемо лише після відповіді.
      onSelect: () => {
        clearRecentRecords().then(
          () => toast({ text: n.recentCleared }),
          () => toast({ text: n.recentClearFailed, tone: 'alert' }),
        );
      },
    },
    // «Вийти» — лише після входу: без входу закривати нічого.
    ...(user?.signedIn
      ? ([
          { kind: 'separator', id: 's4' },
          {
            kind: 'item',
            id: 'sign-out',
            label: t.signOut,
            onSelect: () => void askOrSignOut(),
          },
        ] satisfies MenuItem[])
      : []),
  ];
  /**
   * «Вийти» з незбереженим — спершу діалог. Збереження в дорозі (поле картки зберігається на виході з
   * нього, тобто саме на кліку по цьому меню, REC-16) — не втрата: чекаємо відповіді й дивимось ще
   * раз. Відмова лишає зміни в редакторі — тоді діалог. Наступним кадром: меню спершу закривається
   * й повертає фокус на кнопку, а відповідь знімає «в дорозі» після рендеру.
   */
  const askOrSignOut = async () => {
    if (!hasUnsavedChanges()) {
      doSignOut();
      return;
    }
    await waitForWrites(SIGN_OUT_WRITES_WAIT_MS);
    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (hasUnsavedChanges()) setAskSignOut(true);
    else doSignOut();
  };

  return (
    <>
      <MenuButton
        label={user ? `${t.userMenu}: ${user.name}` : t.userMenu}
        items={items}
        className="btn btn--ghost btn--icon user-btn"
      >
        {user?.id ? (
          <UserAvatar
            userId={user.id}
            avatar={user.avatar}
            name={user.name}
            size="md"
            className="avatar"
          />
        ) : (
          <span className="avatar" aria-hidden="true">
            {user ? user.initials : <Icon name="user" />}
          </span>
        )}
      </MenuButton>
      {askSignOut && (
        <UnsavedDialog
          onSave={null}
          text={{
            title: sv.unsavedTitle,
            body: sv.signOutUnsavedText,
            stay: sv.signOutUnsavedCancel,
            discard: sv.signOutUnsavedConfirm,
          }}
          onStay={() => setAskSignOut(false)}
          onDiscard={() => {
            setAskSignOut(false);
            doSignOut();
          }}
        />
      )}
    </>
  );
}
